import { motion } from "framer-motion";
import { FaXTwitter, FaTiktok } from "react-icons/fa6";
import { Crown, Heart, Sparkles } from "lucide-react";

const PROFILE_PHOTO =
  "https://customer-assets.emergentagent.com/job_854c27b6-3638-4b56-9216-3a4955ff9561/artifacts/uqsz72he_image.png";

const GALLERY = [
  "https://customer-assets.emergentagent.com/job_854c27b6-3638-4b56-9216-3a4955ff9561/artifacts/tdt88m0j_image.png",
  "https://customer-assets.emergentagent.com/job_854c27b6-3638-4b56-9216-3a4955ff9561/artifacts/uqsz72he_image.png",
  "https://customer-assets.emergentagent.com/job_854c27b6-3638-4b56-9216-3a4955ff9561/artifacts/hsgxbd1c_image.png",
];

const LINKS = [
  {
    id: "twitter",
    label: "@melmartinsof",
    sub: "X / Twitter",
    url: "https://x.com/melmartinsof",
    icon: FaXTwitter,
    testId: "link-twitter",
  },
  {
    id: "tiktok",
    label: "@mxlzinhx",
    sub: "TikTok",
    url: "https://www.tiktok.com/@mxlzinhx?_r=1&_t=ZS-96sq942l9V6",
    icon: FaTiktok,
    testId: "link-tiktok",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function LinkInBio() {
  return (
    <div
      data-testid="page-linkinbio"
      className="relative min-h-screen w-full bg-[#050002] text-[#FDF8FA] font-[Outfit] overflow-x-hidden"
      style={{ fontFamily: "Outfit, system-ui, sans-serif" }}
    >
      {/* Decorative backgrounds */}
      <div className="glow-bg" />
      <div className="grain" />

      <main
        data-testid="container-main"
        className="relative w-full max-w-md mx-auto min-h-screen flex flex-col px-6 pt-14 pb-10"
      >
        {/* Hero */}
        <motion.section
          initial="hidden"
          animate="show"
          variants={fadeUp}
          custom={0}
          className="flex flex-col items-center text-center"
        >
          <div className="relative">
            <img
              data-testid="hero-avatar"
              src={PROFILE_PHOTO}
              alt="Mel Martins"
              className="avatar-halo w-32 h-32 rounded-full object-cover border-2 border-[#FF2A7A]"
            />
            <span
              aria-hidden
              className="absolute -bottom-1 -right-1 bg-[#FF2A7A] text-white rounded-full p-1.5 shadow-lg shadow-pink-500/30 border-2 border-[#050002]"
            >
              <Heart size={14} fill="white" />
            </span>
          </div>

          <motion.h1
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="show"
            data-testid="hero-title"
            className="mt-5 text-4xl sm:text-5xl tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
          >
            Mel Martins
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="show"
            data-testid="hero-subtitle"
            className="mt-2 text-sm text-[#B5A4AC] tracking-wide flex items-center gap-1.5"
          >
            <Sparkles size={14} className="text-[#FF2A7A]" />
            seu lugar favorito é aqui
            <Sparkles size={14} className="text-[#FF2A7A]" />
          </motion.p>
        </motion.section>

        {/* Primary CTA */}
        <motion.a
          href="https://privacy.com.br/profile/melmartinsof"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cta-privacy"
          variants={fadeUp}
          custom={3}
          initial="hidden"
          animate="show"
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
          className="shimmer-overlay relative overflow-hidden mt-10 w-full py-4 rounded-full text-white font-semibold text-base flex items-center justify-center gap-2.5 transition-shadow"
          style={{
            background: "linear-gradient(135deg, #FF2A7A 0%, #D4145A 100%)",
            boxShadow:
              "0 10px 32px rgba(255, 42, 122, 0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
            letterSpacing: "0.06em",
          }}
        >
          <Crown size={18} className="relative z-10" />
          <span className="relative z-10">ACESSO VIP</span>
        </motion.a>

        <motion.p
          variants={fadeUp}
          custom={4}
          initial="hidden"
          animate="show"
          className="text-center text-[11px] text-[#B5A4AC] mt-2.5 tracking-wider uppercase"
        >
          conteúdo exclusivo só pra você
        </motion.p>

        {/* Secondary links */}
        <section
          data-testid="links-list"
          className="mt-8 flex flex-col gap-3.5"
        >
          {LINKS.map((link, idx) => {
            const Icon = link.icon;
            return (
              <motion.a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={link.testId}
                variants={fadeUp}
                custom={5 + idx}
                initial="hidden"
                animate="show"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="group w-full py-4 px-5 rounded-full bg-white/[0.04] backdrop-blur-xl border border-white/10 hover:border-[#FF2A7A]/50 hover:bg-white/[0.07] transition-all duration-300 flex items-center gap-4"
              >
                <span className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#FDF8FA] group-hover:bg-[#FF2A7A]/15 group-hover:border-[#FF2A7A]/40 transition-colors">
                  <Icon size={16} />
                </span>
                <span className="flex flex-col items-start leading-tight">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#B5A4AC]">
                    {link.sub}
                  </span>
                  <span className="text-sm font-medium text-[#FDF8FA]">
                    {link.label}
                  </span>
                </span>
                <span className="ml-auto text-[#B5A4AC] group-hover:text-[#FF2A7A] transition-colors text-sm">
                  ↗
                </span>
              </motion.a>
            );
          })}
        </section>

        {/* Gallery */}
        <motion.section
          variants={fadeUp}
          custom={7}
          initial="hidden"
          animate="show"
          className="mt-10"
          data-testid="gallery-section"
        >
          <div className="flex items-center justify-between mb-3 px-1">
            <h2
              className="text-lg text-[#FDF8FA]"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
            >
              prévia
            </h2>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#B5A4AC]">
              deslize →
            </span>
          </div>
          <div
            className="flex overflow-x-auto gap-3 snap-x snap-mandatory pb-2 scrollbar-hide -mx-6 px-6"
            data-testid="gallery-scroll"
          >
            {GALLERY.map((src, idx) => (
              <motion.div
                key={idx}
                whileHover={{ scale: 1.015 }}
                className="shrink-0 w-[72%] aspect-[4/5] snap-center rounded-2xl overflow-hidden relative border border-white/10 shadow-[0_15px_40px_rgba(0,0,0,0.5)]"
                data-testid={`gallery-item-${idx}`}
              >
                <img
                  src={src}
                  alt={`Mel Martins ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <a
                  href="https://privacy.com.br/profile/melmartinsof"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`gallery-cta-${idx}`}
                  className="absolute bottom-3 left-3 right-3 text-center text-xs uppercase tracking-[0.18em] py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-[#FF2A7A]/80 transition-colors"
                >
                  ver mais
                </a>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Footer */}
        <motion.footer
          variants={fadeUp}
          custom={8}
          initial="hidden"
          animate="show"
          data-testid="footer"
          className="mt-12 text-center"
        >
          <div className="flex items-center justify-center gap-2 text-[#FF2A7A] mb-2">
            <span className="h-px w-8 bg-[#FF2A7A]/40" />
            <Heart size={12} fill="#FF2A7A" />
            <span className="h-px w-8 bg-[#FF2A7A]/40" />
          </div>
          <p className="text-[11px] text-[#B5A4AC] tracking-wide">
            Mel Martins © {new Date().getFullYear()}
          </p>
          <p className="text-[10px] text-[#B5A4AC]/70 mt-1">
            todos os direitos reservados
          </p>
        </motion.footer>
      </main>
    </div>
  );
}
